import React from 'react';
import { SearchOutlined, TeamOutlined } from '@ant-design/icons';
import { Row, Col, Table, Popconfirm, Button, message, Input } from 'antd';
import { CreateForm, EditForm } from './index';
import { compareIgnoreCase } from '../../../utility/CompareUtils';
import { isSpecificUser } from './../../../utility/AuthorityUtils';
import { COMPANY_DELETE_ERROR, COMPANY_DELETE_SUCCESS } from './../../../actions/Company';
import { isIE } from './../../../utility/BrowserUtils';

class CompanyManagementForm extends React.Component {
  constructor(props) {
    super(props);
    this.searchInputCompany = React.createRef();
    this.state = {
      filteredInfo: null,
      sortedInfo: null,
      pagination: { defaultPageSize: 50, showSizeChanger: true, size: 'small', position: 'bottom' },
      createCompanyVisible: false,
      editCompanyVisible: false,
      selectedCompany: null,
    };
  }

  /**
   * Action success & errors handling
   * @param prevProps - previous props from earlier state
   */
  componentDidUpdate(prevProps) {
    this.handleActionResponseNotifications(this.props.actionResponse);
  }

  /**
   * Handles notifications for the action response
   * @param actionResponse - the response of the action made
   */
  handleActionResponseNotifications(actionResponse) {
    if (actionResponse === null) {
      return;
    }

    switch (actionResponse.get('type')) {
      case COMPANY_DELETE_ERROR:
        message.error('Der er opstået en fejl, prøv igen.');
        break;

      case COMPANY_DELETE_SUCCESS:
        message.success('Virksomheden blev slettet.');
        break;

      default:
        break;
    }
    this.props.clearActionResponse();
  }

  /**
   * Deletes a company
   * @param company to be deleted
   */
  handleDelete = (company) => {
    this.props.companyDelete(company.id);
  };

  /**
   * Edits a company
   * @param company to be edited
   */
  handleEdit = (company) => {
    this.props.companyEdit(company);
  };

  /**
   * Creates a company
   * @param company to be created
   */
  handleCreate = (company) => {
    this.props.companyCreate(company);
  };

  /**
   * Shows edit window and selects company to show
   * @param company to be edited in modal window
   */
  handleShowEditCompanyModal = (company) => {
    this.setState({ selectedCompany: company });
    this.setState({ editCompanyVisible: true });
  };

  /**
   * Hides edit window and deselects company
   */
  handleHideEditCompanyModal = () => {
    this.setState({ selectedCompany: null });
    this.setState({ editCompanyVisible: false });
  };

  /**
   * Shows create company window
   */
  handleShowCreateCompanyModal = () => {
    this.setState({ createCompanyVisible: true });
  };

  /**
   * Hides create company window
   */
  handleHideCreateCompanyModal = () => {
    this.setState({ createCompanyVisible: false });
  };

  /**
   * Sets filters and sorters on table.
   * @param pagination on table
   * @param filters on table
   * @param sorter on table
   */
  handleChange = (pagination, filters, sorter) => {
    this.setState({
      filteredInfo: filters,
      sortedInfo: sorter,
    });
  };

  /**
   * Filters column values based on search input
   * @param confirm - function to confirm search input
   * @returns {Function}
   */
  handleSearch = (confirm) => () => {
    confirm();
  };

  /**
   * Sets new search value on each input made
   * @param setSelectedKeys - search value to update.
   * @returns {function(*): *}
   */
  handleSearchChange = (setSelectedKeys) => (e) => {
    return setSelectedKeys(e.target.value ? [e.target.value] : []);
  };

  /**
   * Resets column filters
   * @param clearFilters - function to reset filters on column
   * @returns {Function}
   */
  handleReset = (clearFilters) => () => {
    clearFilters();
  };

  /**
   * Parses seconds into years, months and days.
   * @param seconds to parse
   * @returns string showing the resulting years months & days
   */
  generateDateFromSeconds = (seconds) => {
    const years = Math.floor(seconds / 31536000);
    seconds -= years * 31536000;
    const months = Math.floor(seconds / 2592000);
    seconds -= months * 2592000;
    const days = Math.floor(seconds / (3600 * 24));
    return `${years} år, ${months} måneder, ${days} dage`;
  };

  /**
   * Gets the company id from the name of the company.
   * @param companyName is the name of the company
   * @returns {*} the id of the company
   */
  getCompanyIdFromCompanyName = (companyName) => {
    for (let i = 0; i < this.props.companies.size; i++) {
      if (companyName === this.props.companies.get(i).get('name')) {
        return this.props.companies.get(i).get('id');
      }
    }
    return -1;
  };

  /**
   * Sets if a column should have a filter function or not.
   * @param companies to filter on
   * @returns {*} filters
   */
  createCompaniesFilters = (companies) => {
    if (companies.size < 2) {
      return null;
    }

    const filters = [];
    for (let i = 0; i < companies.size; i++) {
      const nameValue = companies.get(i).get('name');
      filters.push({
        text: nameValue,
        value: nameValue,
      });
    }
    return filters;
  };

  /**
   * Creates columns for table
   * @returns {*[]} columns
   */
  createTableColumns(companies) {
    let { sortedInfo, filteredInfo } = this.state;
    sortedInfo = sortedInfo || {};
    filteredInfo = filteredInfo || {};
    return [
      {
        title: 'Virksomhed',
        dataIndex: 'name',
        key: 'companyName',
        width: '20%',
        filterDropdown:
          companies.size < 2
            ? null
            : ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
                this.createFilterDropDown(
                  setSelectedKeys,
                  selectedKeys,
                  confirm,
                  clearFilters,
                  this.searchInputCompany,
                  'Søg virksomhed'
                ),
        filterIcon: (filtered) => (
          <SearchOutlined
            className={
              filtered ? 'custom-filter-dropdown-icon-active' : 'custom-filter-dropdown-icon'
            }
          />
        ),
        onFilter: (value, record) => record.name.toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownVisibleChange: (visible) => {
          if (visible) {
            setTimeout(() => {
              this.searchInputCompany.current.focus();
            });
          }
        },
        sorter: companies.size < 2 ? false : (a, b) => compareIgnoreCase(a.name, b.name),
        sortOrder: sortedInfo.columnKey === 'companyName' && sortedInfo.order,
      },
      {
        title: 'Logning',
        dataIndex: 'loggingEnabled',
        key: 'loggingEnabled',
        width: '10%',
        sorter: false,
        filters:
          companies.size < 2
            ? null
            : [
                {
                  text: 'Ja',
                  value: true,
                },
                {
                  text: 'Nej',
                  value: false,
                },
              ],
        filteredValue: filteredInfo.loggingEnabled || null,
        onFilter: (value, record) => record.loggingEnabled.toString() === value,
        render: (loggingEnabled, record, index) => {
          return loggingEnabled ? 'Ja' : 'Nej';
        },
      },
      {
        title: 'Log opbevaringstid',
        dataIndex: 'logRetentionTime',
        key: 'logRetentionTime',
        width: '20%',
        sorter: false,
        render: (text, record, index) => {
          return this.generateDateFromSeconds(text);
        },
      },
      {
        title: 'Klientid',
        dataIndex: 'clientId',
        key: 'clientId',
        width: '44%',
        sorter: (a, b) => compareIgnoreCase(a.clientId, b.clientId),
        sortOrder: sortedInfo.columnKey === 'clientId' && sortedInfo.order,
      },
      {
        title: '',
        dataIndex: 'edit',
        key: 'edit',
        width: '10%',
        sorter: false,
        render: (text, record, index) => {
          return (
            <Button type={'primary'} onClick={() => this.handleShowEditCompanyModal(record)}>
              Rediger
            </Button>
          );
        },
      },
      {
        title: '',
        dataIndex: 'delete',
        key: 'delete',
        width: '2%',
        sorter: false,
        render: (text, record, index) => {
          return (
            <Popconfirm
              title={'Er du sikker på du vil slette virksomheden?'}
              onConfirm={() => this.handleDelete(record)}
            >
              <Button type={'danger'}>X</Button>
            </Popconfirm>
          );
        },
      },
    ];
  }

  /**
   * Due to IE focus issue on input as subcomponent, additional focus handling must be made.
   * @param e - e is the event which holds target(input)
   */
  handleLostFocus = (e) => {
    if (isIE) {
      e.target.focus();
      e.target.selectionStart += e.target.value.length;
      e.target.selectionEnd = e.target.value.length;
    }
  };

  /**
   * Creates a search input field to filter with.
   * @param setSelectedKeys - the function to either clear or set value of the field
   * @param selectedKeys - the value of the input.
   * @param confirm - the function to confirm the input as search value
   * @param clearFilters - the function to clear filters
   * @param inputRef - the element as ref
   * @param placeholder - the placeholder for the input field
   * @returns {*}
   */
  createFilterDropDown = (
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
    inputRef,
    placeholder
  ) => {
    return (
      <div className='custom-filter-dropdown'>
        <Input
          ref={(ele) => (inputRef.current = ele)}
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={this.handleSearchChange(setSelectedKeys)}
          onPressEnter={this.handleSearch(confirm)}
          onBlur={this.handleLostFocus}
        />
        <Button type='primary' onClick={this.handleSearch(confirm)}>
          Search
        </Button>
        <Button onClick={this.handleReset(clearFilters)}>Reset</Button>
      </div>
    );
  };

  render() {
    return (
      <Row>
        <Col span={24}>
          <Row>
            <Col span={12}>
              <div className={'div-title'}>
                <TeamOutlined />
                <label className={'spacing-left'}>Virksomhedsindstillinger</label>
              </div>
            </Col>
            <Col span={12}>
              {isSpecificUser(this.props.user, ['ADMIN']) ? (
                <Button
                  type={'primary'}
                  className={'float-right'}
                  onClick={this.handleShowCreateCompanyModal}
                >
                  Opret virksomhed
                </Button>
              ) : null}
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Table
                className={'spacing-top'}
                columns={this.createTableColumns(this.props.companies)}
                dataSource={this.props.companies.toJS()}
                rowKey={(record) => record.id}
                onChange={this.handleChange}
                pagination={this.state.pagination}
                loading={this.props.isLoading}
              />
              <CreateForm
                visible={this.state.createCompanyVisible}
                actionResponse={this.props.actionResponse}
                isLoading={this.props.isLoading}
                clearActionResponse={this.props.clearActionResponse}
                getCompanyIdFromCompanyName={this.getCompanyIdFromCompanyName}
                onCreate={this.handleCreate}
                onCancel={this.handleHideCreateCompanyModal}
              />
              {this.state.selectedCompany ? (
                <EditForm
                  company={this.state.selectedCompany}
                  visible={this.state.editCompanyVisible}
                  isLoading={this.props.isLoading}
                  actionResponse={this.props.actionResponse}
                  clearActionResponse={this.props.clearActionResponse}
                  getCompanyIdFromCompanyName={this.getCompanyIdFromCompanyName}
                  onEdit={this.handleEdit}
                  onCancel={this.handleHideEditCompanyModal}
                  generateDateFromSeconds={this.generateDateFromSeconds}
                />
              ) : null}
            </Col>
          </Row>
        </Col>
      </Row>
    );
  }
}

export default CompanyManagementForm;
